import React, { useEffect, useRef, useState } from 'react';
import { useSeasonState } from './store';

/**
 * Baba verde que escurre desde arriba sobre toda la página, al estilo del logo de
 * Goosebumps. Adaptación del "dripping slime effect" en canvas: el original pinta
 * blobs blancos sobre negro y funde con `blur + contrast`; aquí el lienzo es
 * transparente para verse SOBRE la interfaz, así que la fusión de gotas la hace un
 * filtro SVG "goo" sobre el canal alfa y la estela se borra con `destination-out`.
 *
 * Dura unos segundos y se desvanece; nunca recibe el puntero, así que el auditor
 * puede seguir trabajando debajo.
 */
const DURATION_MS = 9000;
const FADE_MS = 1200;
const SLIME = '#65ff00'; // "green slime"; Goosebumps no publica un hex oficial

interface Drip {
  x: number;
  y: number;
  radius: number;
  speedX: number;
  speedY: number;
  angle: number;
  va: number;
  range: number;
  gravity: number;
  vy: number;
}

const newDrip = (width: number): Drip => {
  const radius = Math.random() * 80 + 20;
  return {
    radius,
    x: 2 * radius + Math.random() * (width - radius * 4),
    y: -radius,
    speedX: Math.random() * 0.2 - 0.1,
    speedY: Math.random() * 0.5 + 0.2,
    angle: 0,
    va: Math.random() * 0.1 - 0.05,
    range: Math.random() * 10,
    gravity: Math.random() * 0.005,
    vy: 0,
  };
};

const runSlime = (canvas: HTMLCanvasElement, stop: () => boolean) => {
  const ctx = canvas.getContext('2d');
  if (!ctx) return () => undefined;
  canvas.width = window.innerWidth;
  canvas.height = window.innerHeight;
  let drips: Drip[] = Array.from({ length: 20 }, () => newDrip(canvas.width));
  ctx.lineWidth = 50;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';

  const update = (d: Drip) => {
    if (d.x < d.radius || d.x > canvas.width - d.radius) d.speedX *= -1;
    if (d.y > canvas.height + d.radius) {
      Object.assign(d, newDrip(canvas.width), { radius: Math.random() * 120 + 30 });
    }
    if (d.y > d.radius) {
      d.vy += d.gravity;
      d.speedY += d.vy;
      d.angle += d.va;
    }
    if (d.y > d.radius * 2) d.radius -= 0.15;
    // Una gota agotada se recicla arriba: `arc` no admite radios negativos.
    if (d.radius < 1) Object.assign(d, newDrip(canvas.width));
    d.x += d.speedX * Math.cos(d.angle) * d.range;
    d.y += d.speedY;
  };

  let frame = 0;
  const animate = () => {
    if (stop()) return;
    // Estela: en vez de repintar negro semitransparente, se resta alfa.
    ctx.globalCompositeOperation = 'destination-out';
    ctx.fillStyle = 'rgba(0,0,0,0.1)';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.globalCompositeOperation = 'source-over';
    ctx.fillStyle = SLIME;
    ctx.strokeStyle = SLIME;
    drips.forEach(update);
    drips.forEach((d) => {
      ctx.beginPath();
      ctx.arc(d.x, d.y, Math.max(d.radius, 0), 0, Math.PI * 2);
      ctx.fill();
    });
    const maxDistance = 150;
    for (let a = 0; a < drips.length; a++) {
      for (let b = a; b < drips.length; b++) {
        const dist = Math.hypot(drips[a].x - drips[b].x, drips[a].y - drips[b].y);
        if (dist < maxDistance) {
          ctx.beginPath();
          ctx.moveTo(drips[a].x, drips[a].y);
          ctx.lineTo(drips[b].x, drips[b].y);
          ctx.stroke();
        }
      }
      if (drips[a].radius < 20 && drips[a].y > canvas.height) {
        drips.splice(a, 1);
        a--;
      }
    }
    frame = window.requestAnimationFrame(animate);
  };
  const onResize = () => {
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;
    drips = drips.map(() => newDrip(canvas.width));
  };
  window.addEventListener('resize', onResize);
  frame = window.requestAnimationFrame(animate);
  return () => {
    window.cancelAnimationFrame(frame);
    window.removeEventListener('resize', onResize);
  };
};

interface SlimeOverlayProps {
  /** Con movimiento reducido los disparos se ignoran: no arranca ninguna corrida (R10). */
  disabled?: boolean;
}

const SlimeOverlay: React.FC<SlimeOverlayProps> = ({ disabled = false }) => {
  const { effect, effectSeq } = useSeasonState();
  const [run, setRun] = useState<number | null>(null);
  const [fading, setFading] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  // Último disparo atendido. Un disparo se atiende (o se ignora) UNA vez: si `disabled`
  // cambia después, no se reproduce (E07-F01, R10: con movimiento reducido el disparo
  // se ignora, no se aplaza; y una corrida terminada no se repite al cambiar la
  // preferencia). Por eso la comparación va ANTES de mirar `disabled`.
  const handledSeq = useRef(0);

  // Cada disparo (effectSeq) arranca una corrida nueva, aunque la anterior siga.
  useEffect(() => {
    if (effectSeq === handledSeq.current) return;
    handledSeq.current = effectSeq;
    if (disabled) return;
    if (effect === 'slime' && effectSeq > 0) {
      setFading(false);
      setRun(effectSeq);
    }
  }, [effect, effectSeq, disabled]);

  useEffect(() => {
    if (run === null || !canvasRef.current) return undefined;
    let stopped = false;
    const cleanup = runSlime(canvasRef.current, () => stopped);
    const fadeTimer = window.setTimeout(() => setFading(true), DURATION_MS - FADE_MS);
    const endTimer = window.setTimeout(() => {
      stopped = true;
      setRun(null);
      setFading(false);
    }, DURATION_MS);
    return () => {
      stopped = true;
      cleanup();
      window.clearTimeout(fadeTimer);
      window.clearTimeout(endTimer);
    };
  }, [run]);

  if (run === null) return null;

  return (
    <div className={`slime-overlay ${fading ? 'slime-overlay--fading' : ''}`} aria-hidden="true">
      <svg width="0" height="0" style={{ position: 'absolute' }}>
        <defs>
          <filter id="seasonal-goo">
            <feGaussianBlur in="SourceGraphic" stdDeviation="14" result="blur" />
            <feColorMatrix
              in="blur"
              mode="matrix"
              values="1 0 0 0 0  0 1 0 0 0  0 0 1 0 0  0 0 0 28 -10"
              result="goo"
            />
          </filter>
        </defs>
      </svg>
      <canvas key={run} ref={canvasRef} className="slime-overlay__canvas" />
    </div>
  );
};

export default SlimeOverlay;
